import React from 'react';
import {useParams} from 'react-router-dom';
import {useSelector} from 'react-redux';
import {SimpleTabs, Notes, FilesComponent, Comments} from 'components';
import {
    ClientGeneral,
    ClientContact,
    TabsHeader,
    ClientEnrollment,
    ClientAssignment,
    ClientAvailabilitySchedule,
    Authorization,
} from './core';
import {commentModels, fileEnums, OrgIntegrations, PermissionList, RolePermission} from 'utils';
import {HistoryTab} from '../../history';
import {canSeeClientDiagnoses, Clinical, Goals} from './clientClinical';
import {eventOnModel, Events} from 'fragments';
import {ClientDocumentation} from './core/clientDocumentation';

export const ClientItem = () => {
    const params = useParams();
    const data = useSelector((state) => state.client.clientItemInfo);
    const {cSession} = OrgIntegrations();
    // Clinical = Diagnoses (no cSession needed — billing reads them) + Assessment (cSession only)
    const showClinical = canSeeClientDiagnoses() || (cSession && RolePermission([PermissionList.CLIENT_ASSESSMENT_READ?.code]));

    const tabsLabels = [
        RolePermission([PermissionList.CLIENT_READ?.code, PermissionList.CLIENT_CREATE?.code, PermissionList.CLIENT_STATUS?.code]) && {
            label: 'General',
        },

        RolePermission([PermissionList.CLIENT_CONTACT_MANAGE?.code, PermissionList.CLIENT_CONTACT_READ?.code]) && {label: 'Contacts'},
        RolePermission([PermissionList.CLIENT_ENROLLMENT_MANAGE?.code, PermissionList.CLIENT_ENROLLMENT_READ?.code]) && {label: 'Payers'},
        RolePermission([PermissionList.CLIENT_AUTHORIZATION_READ?.code, PermissionList.CLIENT_AUTHORIZATION_MANAGE?.code]) && {
            label: 'Authorization',
        },
        showClinical && {label: 'Medical'},


        RolePermission([PermissionList.CLIENT_AVAILABILITY_READ?.code]) &&
        {label: 'Availability'},
        RolePermission([PermissionList.CLIENT_STAFF_ASSIGNMENT_READ?.code, PermissionList.CLIENT_STAFF_ASSIGNMENT?.code]) && {label: 'Clinicians'},

        cSession && {label: 'Events'},
        cSession && RolePermission([PermissionList.GOAL_READ?.code]) && {label: 'Goals'},
        // cSession &&  RolePermission([PermissionList.CLIENT_ABC_READ?.code]) && {label: 'Abc'},
        // cSession && { label: 'Progress Report' },

        RolePermission([
            PermissionList.CLIENT_FORM_READ?.code,
            PermissionList.CLIENT_COMMENT_READ?.code,
            PermissionList.CLIENT_EVENT_READ?.code,
            // PermissionList.CLIENT_ABC_READ?.code,
        ]) &&
        {label: 'Forms'},
        {label: 'Comments'},

        // { label: 'Sections' },

        // RolePermission([PermissionList.CLIENT_NOTE_READ?.code, PermissionList.CLIENT_NOTE_MANAGE?.code]) && { label: 'Notes' },
        RolePermission([PermissionList.CLIENT_HISTORY_READ?.code]) && {label: 'History'},
        // { label: 'Forms' },
        RolePermission([PermissionList.CLIENT_FILE_READ?.code, PermissionList.CLIENT_FILE_MANAGE?.code]) && {label: 'Files'},
    ];

    const tabsContent = [
        RolePermission([PermissionList.CLIENT_READ?.code, PermissionList.CLIENT_CREATE?.code, PermissionList.CLIENT_STATUS?.code]) && {
            tabComponent: <ClientGeneral data={data}/>,
            tab: 'General',
        },
        RolePermission([PermissionList.CLIENT_CONTACT_MANAGE?.code, PermissionList.CLIENT_CONTACT_READ?.code]) && {
            tabComponent: <ClientContact/>,
            tab: 'Contacts',
        },
        RolePermission([PermissionList.CLIENT_ENROLLMENT_MANAGE?.code, PermissionList.CLIENT_ENROLLMENT_READ?.code]) && {
            tabComponent: <ClientEnrollment/>,
            tab: 'Payers',
        },
        RolePermission([PermissionList.CLIENT_AUTHORIZATION_READ?.code, PermissionList.CLIENT_AUTHORIZATION_MANAGE?.code]) && {
            tabComponent: <Authorization/>,
            tab: 'Authorization',
        },
        showClinical && {
            tabComponent: <Clinical/>,
            tab: 'Medical',
        },
        RolePermission([PermissionList.CLIENT_AVAILABILITY_READ?.code]) &&
        {
            tabComponent: <ClientAvailabilitySchedule/>,
            tab: 'Availability',
        },

        RolePermission([PermissionList.CLIENT_STAFF_ASSIGNMENT_READ?.code, PermissionList.CLIENT_STAFF_ASSIGNMENT?.code]) && {
            tabComponent: <ClientAssignment/>,
            tab: 'Clinicians',
        },

        cSession && {
            tabComponent: <Events model={{resource: params?.id, onModel: eventOnModel.Client}}/>,
            tab: 'Events',
        },
        cSession &&
        RolePermission([PermissionList.GOAL_READ?.code]) && {
            tabComponent: <Goals/>,
            tab: 'Goals',
        },
        // cSession &&  RolePermission([PermissionList.CLIENT_ABC_READ?.code]) &&{
        //     tabComponent: <Abc/>,
        //     tab: 'Abc',
        // },
        // cSession && {
        //     tabComponent: <ProgressReport />,
        //     tab: 'Progress Report',
        // },
        RolePermission([
            PermissionList.CLIENT_FORM_READ?.code,
            PermissionList.CLIENT_COMMENT_READ?.code,
            PermissionList.CLIENT_EVENT_READ?.code,
            // PermissionList.CLIENT_ABC_READ?.code,
        ]) &&
        {
            tabComponent: <ClientDocumentation/>,
            tab: 'Forms',
        },

        // {
        //     tabComponent: <ClientSections />,
        //     // tabComponent: <ClientNotes id={data?.id}/>,
        //     tab: 'Sections',
        // },
        //
        // RolePermission([PermissionList.CLIENT_NOTE_READ?.code, PermissionList.CLIENT_NOTE_MANAGE?.code]) && {
        //     tabComponent: <ClientGeneralNotes id={data?.id} />,
        //     // tabComponent: <ClientNotes id={data?.id}/>,
        //     tab: 'Notes',
        // },
        {
            tabComponent: (
                <Comments
                    fromDetail={true}
                    resource={commentModels.Client}
                    resourceId={params?.id}
                    label={'Client'}
                    width={700}
                    height={600}
                    attachmentPurpose={'client'}
                    enableAiImprove
                />
            ),
            tab: 'Comments',
        },

        RolePermission([PermissionList.CLIENT_HISTORY_READ?.code]) && {
            tabComponent: <HistoryTab currentId={params?.id} onModel={'Client'}/>,
            tab: 'History',
        },

        // RolePermission([PermissionList.CLIENT_HISTORY_READ?.code]) &&
        // {
        //     tabComponent: <ClientForms/>,
        //     tab: 'Forms',
        // },
        RolePermission([PermissionList.CLIENT_FILE_READ?.code, PermissionList.CLIENT_FILE_MANAGE?.code]) && {
            tabComponent: (
                <FilesComponent onResource={fileEnums.CLIENT} permissions={[PermissionList.CLIENT_FILE_MANAGE?.code]}
                                shareBtn={true} withExpiration={true}/>
            ),
            tab: 'Files',
        },
    ];

    return (
        <>
            <div className="tab-box-wrapper">
                <TabsHeader data={data}/>
                <SimpleTabs
                    defaultTab={
                        RolePermission([
                            PermissionList.CLIENT_READ?.code,
                            PermissionList.CLIENT_CREATE?.code,
                            PermissionList.CLIENT_STATUS?.code,
                        ])
                            ? 'General'
                            : ''
                    }
                    tabsLabels={tabsLabels}
                    tabsContent={tabsContent}
                />
            </div>
        </>
    );
};
